import { useState, useCallback, useEffect, useMemo } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { AccordionSection } from '../../components/checkout/AccordionSection';
import { CartSummary } from '../../components/checkout/CartSummary';
import { AddressSection } from '../../components/checkout/AddressSection';
import { BillingSection } from '../../components/checkout/BillingSection';
import { DeliveryDateSection } from '../../components/checkout/DeliveryDateSection';
import { PaymentSection } from '../../components/checkout/PaymentSection';
import { EmailOtpSection } from '../../components/checkout/EmailOtpSection';
import { UserDataSection } from '../../components/checkout/UserDataSection';
import { getCSRFToken } from '../../utils/csrf';
import { resolveWholesaleSurcharge } from '../../utils/wholesaleSurcharge';
import { route } from '../../utils/route';
import { trackStartCheckout, trackCheckoutStepViewed, trackCheckoutStepCompleted } from '../../utils/tracking';
import type {
  CheckoutProps,
  CheckoutStep,
  CheckoutUser,
  CheckoutAddress,
  CheckoutCart,
  DeliveryDate,
  PaymentMethods,
  DiscountInfo,
  QuickCheckoutDefaults,
  PendingOrder,
  GiftData,
} from '../../types/checkout';
import { QuickCheckoutView } from '../../components/checkout/QuickCheckoutView';
import { PendingOrdersGate } from '../../components/checkout/PendingOrderWarning';
import { MinOrderAlert } from '../../components/cart/MinOrderAlert';

declare const dataLayer: unknown[];

function getInitialStep(mode: 'fast' | 'standard', isLoggedIn: boolean, user: CheckoutUser | null, hasOrg: boolean): CheckoutStep {
  // Standard mode never shows email step (user is already authenticated)
  if (mode === 'standard') {
    if (!user?.is_complete) return 'userData';
    return hasOrg ? 'billing' : 'address';
  }
  // Fast mode: show email step for guests
  if (!isLoggedIn) return 'email';
  if (!user?.is_complete) return 'userData';
  return hasOrg ? 'billing' : 'address';
}

export default function Checkout({
  mode = 'fast',
  isLoggedIn: initialLoggedIn,
  user: initialUser,
  addresses: initialAddresses,
  cart: initialCart,
  paymentMethods: initialPaymentMethods,
  accRecOverLimit: initialAccRecOverLimit,
  accRecName: initialAccRecName,
  mercadoPagoEnabled,
  ga,
  discountNeighborhood,
  discountLocationId,
  discountDeliverySlot,
  directPaymentDiscount,
  allowsDiscountCode,
  quickCheckout,
  organization,
  pendingOrders: initialPendingOrders = [],
  showQrOption: serverShowQr = false,
  giftData = null,
}: CheckoutProps) {
  const isFastMode = mode === 'fast';
  const isGiftMode = !!giftData;
  const hasBillingStep = !!organization && organization.companies.length > 0;
  const personalDisabled = organization?.personal_disabled ?? false;
  const [showQuickCheckout, setShowQuickCheckout] = useState(!!quickCheckout);

  // Restore quick checkout when user navigates back from the modify view
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (quickCheckout && !e.state?._checkoutModify) {
        setShowQuickCheckout(true);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [quickCheckout]);

  // State
  const [currentStep, setCurrentStep] = useState<CheckoutStep>(
    () => getInitialStep(mode, initialLoggedIn, initialUser, hasBillingStep)
  );
  const [isLoggedIn, setIsLoggedIn] = useState(initialLoggedIn);
  const [user, setUser] = useState<CheckoutUser | null>(initialUser);
  const [addresses, setAddresses] = useState<CheckoutAddress[]>(initialAddresses);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    giftData?.address_id ?? (initialAddresses.length > 0 ? initialAddresses[0].id : null)
  );
  const [deliveryDates, setDeliveryDates] = useState<DeliveryDate[]>([]);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number | null>(
    () => (!initialAccRecOverLimit && initialPaymentMethods[2] != null) ? 2 : null
  );
  const [pMethods, setPMethods] = useState<PaymentMethods>(() => {
    if (isGiftMode) {
      // Filter out Contado (0) and Cuenta Corriente (2) for gift orders
      const filtered: PaymentMethods = {};
      for (const [k, v] of Object.entries(initialPaymentMethods)) {
        if (k !== '0' && k !== '2') filtered[Number(k)] = v;
      }
      return filtered;
    }
    return initialPaymentMethods;
  });
  const [accRecOverLimit, setAccRecOverLimit] = useState(initialAccRecOverLimit);
  const [accRecName, setAccRecName] = useState<string | null>(initialAccRecName);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useQr, setUseQr] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [addressDiscountWarning, setAddressDiscountWarning] = useState<string | null>(null);
  const [slotDiscountWarning, setSlotDiscountWarning] = useState<string | null>(null);

  // Billing step state: which company to invoice (null = not yet chosen or personal)
  // We use 'personal' sentinel to distinguish "chose personal" from "not chosen yet"
  const [billingCompanyId, setBillingCompanyId] = useState<number | null>(null);
  const [billingChosen, setBillingChosen] = useState(false);
  const [directPayDiscount, setDirectPayDiscount] = useState(directPaymentDiscount ?? null);

  // Filter addresses based on billing selection
  const filteredAddresses = useMemo(() => {
    if (!hasBillingStep || !billingChosen) return addresses;
    if (billingCompanyId !== null) {
      // Company selected: show org addresses that either:
      // 1. belong explicitly to this company (company_id matches)
      // 2. have no company assigned AND (this company is the org default, or org has no default)
      const orgDefaultId = organization!.default_company_id;
      const isDefaultOrNoDefault = !orgDefaultId || orgDefaultId === billingCompanyId;
      return addresses.filter(a => {
        if (a.organization_id !== organization!.id) return false;
        if (a.company_id === billingCompanyId) return true;
        if (!a.company_id && isDefaultOrNoDefault) return true;
        return false;
      });
    }
    // Personal: show only personal addresses
    return addresses.filter(a => !a.organization_id);
  }, [addresses, billingCompanyId, billingChosen, hasBillingStep, organization]);

  // Cart state (can be updated when discount changes)
  const [cart, setCart] = useState<CheckoutCart>(initialCart);

  // Discount management state
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [isLoadingDiscount, setIsLoadingDiscount] = useState(false);
  const [previewData, setPreviewData] = useState<{
    code: string;
    discount: number;
    discountPercentage: number;
    discountableItems: string[];
    discountInfo: DiscountInfo;
  } | null>(null);

  // Pending orders gate state
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>(initialPendingOrders);
  const [pendingOrdersResolved, setPendingOrdersResolved] = useState(initialPendingOrders.length === 0);

  // Track checkout start on mount
  useEffect(() => {
    if (!initialCart.isEmpty) {
      trackStartCheckout(initialCart.total, initialCart.items.length);
    }
  }, []);

  // Track checkout step views
  useEffect(() => {
    trackCheckoutStepViewed(currentStep, cart.total);
  }, [currentStep]);

  // Selected address object (from filtered list when billing is active)
  const selectedAddress = useMemo((): CheckoutAddress | null => {
    if (selectedAddressId === null) return null;
    return filteredAddresses.find(a => a.id === selectedAddressId) || null;
  }, [selectedAddressId, filteredAddresses]);

  // Helper to check if an address is compatible with the discount's restrictions
  const isAddressCompatibleWithDiscount = useCallback((address: CheckoutAddress | null): boolean => {
    if (!address) return true;
    // Location restriction (nearby offer): must match exact address
    if (discountLocationId) {
      return address.id === discountLocationId;
    }
    // Neighborhood restriction: must match neighborhood
    if (discountNeighborhood) {
      if (!address.is_country) return false;
      return address.neighborhood?.toLowerCase().trim() === discountNeighborhood.toLowerCase().trim();
    }
    return true;
  }, [discountNeighborhood, discountLocationId]);

  // Helper to check if a delivery date is compatible with the discount's slot restriction
  // If the discount's slot is not in the available dates, any date is valid (discount falls back to first available)
  const isDeliveryDateCompatibleWithDiscount = useCallback((deliveryDate: string | null): boolean => {
    if (!discountDeliverySlot || !deliveryDate) return true;

    // If the selected date matches the discount slot, it's valid
    if (deliveryDate === discountDeliverySlot) return true;

    // If the discount's slot is NOT in the available dates, the discount falls back
    // to the first available slot - so any selected date is valid
    const isDiscountSlotAvailable = deliveryDates.some(d => d.value === discountDeliverySlot);
    if (!isDiscountSlotAvailable) return true;

    // The discount slot is available but user selected a different date
    return false;
  }, [discountDeliverySlot, deliveryDates]);

  // Effective discount: 0 if the selected address or delivery date is incompatible
  const effectiveDiscount = useMemo(() => {
    const addressOk = !selectedAddress || isAddressCompatibleWithDiscount(selectedAddress);
    const slotOk = isDeliveryDateCompatibleWithDiscount(selectedDeliveryDate);
    if (addressOk && slotOk) {
      return cart.discount || 0;
    }
    return 0;
  }, [selectedAddress, selectedDeliveryDate, cart.discount, isAddressCompatibleWithDiscount, isDeliveryDateCompatibleWithDiscount]);

  // Build warning message for delivery slot restriction
  const buildSlotWarning = useCallback(() => {
    const displayName = cart.discountInfo?.display_name || cart.discountCode;
    const slotLabel = cart.discountInfo?.delivery_slot || discountDeliverySlot;
    return `El descuento "${displayName}" solo aplica para el turno del ${slotLabel}. Si elegís otro turno, el descuento no se aplicará.`;
  }, [cart.discountInfo, cart.discountCode, discountDeliverySlot]);

  // Update delivery slot warning when delivery date changes
  useEffect(() => {
    if (!discountDeliverySlot || !selectedDeliveryDate) {
      setSlotDiscountWarning(null);
      return;
    }
    if (!isDeliveryDateCompatibleWithDiscount(selectedDeliveryDate)) {
      setSlotDiscountWarning(buildSlotWarning());
    } else {
      setSlotDiscountWarning(null);
    }
  }, [selectedDeliveryDate, discountDeliverySlot, isDeliveryDateCompatibleWithDiscount, buildSlotWarning]);

  // Adjusted cart with effective discount for CartSummary
  // Keep discountCode and discountInfo even if discount doesn't apply (so user can see/manage their code)
  // Only adjust the actual discount amount shown in totals
  const adjustedCart = useMemo(() => ({
    ...cart,
    discount: effectiveDiscount,
    // For totals display: only show code in totals if discount is being applied
    // But the DiscountCodeInput will still receive cart.discountCode and cart.discountInfo
  }), [cart, effectiveDiscount]);

  // Wholesale items carry a Mercado Pago surcharge (#60). Every summary that
  // shows a total reads this same value, so they can't drift apart.
  const wholesaleSurcharge = useMemo(() => resolveWholesaleSurcharge({
    wholesaleSubtotal: cart.wholesaleSubtotal,
    percent: cart.wholesaleSurchargePercent,
    selectedMethod: selectedPaymentMethod,
    useQr,
  }), [cart.wholesaleSubtotal, cart.wholesaleSurchargePercent, selectedPaymentMethod, useQr]);

  const hasWholesaleItems = (cart.wholesaleSubtotal ?? 0) > 0
    && (cart.wholesaleSurchargePercent ?? 0) > 0;

  // Min order validation
  const minOrder = cart.minOrder ?? 0;
  const cartSubtotal = cart.subtotal ?? cart.total;
  const hasUnfulfilledMinimum = minOrder > 0 && cartSubtotal < minOrder;

  // API helper
  const apiCall = useCallback(async <T,>(url: string, options: RequestInit = {}): Promise<T & { success: boolean; message: string }> => {
    setApiLoading(true);
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          ...options.headers as Record<string, string>,
        },
        ...options,
      });
      const data = await res.json();
      if (!res.ok) return { success: false, message: data.message || 'Error', ...data };
      return { success: true, message: data.message || 'OK', ...data };
    } catch (err) {
      return { success: false, message: 'Error de conexión' } as T & { success: boolean; message: string };
    } finally {
      setApiLoading(false);
    }
  }, []);

  // Refresh cart data (e.g., after discount changes)
  const refreshCart = useCallback(async () => {
    try {
      const result = await apiCall<{ cart?: CheckoutCart }>(route('api.checkout.state'), { method: 'GET' });
      if (result.success && result.cart) {
        setCart(result.cart);
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }, [apiCall]);

  // Discount management callbacks
  const handlePreviewDiscount = useCallback(async (code: string) => {
    if (!code.trim()) {
      setPreviewData(null);
      return;
    }

    setIsLoadingDiscount(true);
    setDiscountError(null);

    try {
      const response = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(code)}&preview=1`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Código inválido');
      }

      const data = await response.json();

      setPreviewData({
        code,
        discount: data.discount,
        discountPercentage: data.discount_percentage,
        discountableItems: data.discountable_items ?? [],
        discountInfo: data.discount_info,
      });

      setIsLoadingDiscount(false);
    } catch (error) {
      setPreviewData(null);
      setDiscountError(error instanceof Error ? error.message : 'Código inválido');
      setIsLoadingDiscount(false);
    }
  }, []);

  const handleConfirmDiscount = useCallback(async () => {
    if (!previewData) return;

    setIsLoadingDiscount(true);

    try {
      const response = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(previewData.code)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to apply discount');
      }

      setPreviewData(null);
      setIsLoadingDiscount(false);

      // Refresh cart to get updated totals
      await refreshCart();
    } catch (error) {
      setIsLoadingDiscount(false);
    }
  }, [previewData, refreshCart]);

  const handleCancelPreview = useCallback(() => {
    setPreviewData(null);
  }, []);

  const handleApplyDiscount = useCallback(async (code: string) => {
    if (!code.trim()) return;

    setIsLoadingDiscount(true);
    setDiscountError(null);

    try {
      const response = await fetch(`${route('api.cart.get_discount')}?code=${encodeURIComponent(code)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Código inválido');
      }

      setIsLoadingDiscount(false);

      // Refresh cart to get updated totals
      await refreshCart();
    } catch (error) {
      setDiscountError(error instanceof Error ? error.message : 'Código inválido');
      setIsLoadingDiscount(false);
    }
  }, [refreshCart]);

  const handleClearDiscount = useCallback(async () => {
    setIsLoadingDiscount(true);

    try {
      await fetch(route('api.cart.clear_discount'), {
        method: 'DELETE',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error clearing discount:', error);
    }

    setIsLoadingDiscount(false);

    // Refresh cart to get updated totals
    await refreshCart();
  }, [refreshCart]);

  // Step completion
  const isStepComplete = useCallback((step: CheckoutStep): boolean => {
    switch (step) {
      case 'email': return isLoggedIn;
      case 'userData': return !!user?.is_complete;
      case 'billing': return billingChosen;
      case 'address': return filteredAddresses.length > 0 && !!selectedAddressId;
      case 'deliveryDate': return !!selectedDeliveryDate;
      case 'payment': return selectedPaymentMethod !== null;
      default: return false;
    }
  }, [isLoggedIn, user, billingChosen, filteredAddresses, selectedAddressId, selectedDeliveryDate, selectedPaymentMethod]);

  const canProceedToStep = useCallback((step: CheckoutStep): boolean => {
    const billingOk = !hasBillingStep || billingChosen;
    switch (step) {
      case 'email': return isFastMode; // Only available in fast mode
      case 'userData': return isLoggedIn;
      case 'billing': return isLoggedIn && !!user?.is_complete;
      case 'address': return isLoggedIn && !!user?.is_complete && billingOk;
      case 'deliveryDate': return isLoggedIn && !!user?.is_complete && billingOk && filteredAddresses.length > 0;
      case 'payment': return isLoggedIn && !!user?.is_complete && billingOk && filteredAddresses.length > 0 && !!selectedAddressId && !!selectedDeliveryDate;
      default: return false;
    }
  }, [isFastMode, isLoggedIn, user, hasBillingStep, billingChosen, filteredAddresses, selectedAddressId, selectedDeliveryDate]);

  const handleStepClick = useCallback((step: CheckoutStep) => {
    if (canProceedToStep(step)) setCurrentStep(step);
  }, [canProceedToStep]);

  // Dynamic step numbering
  const stepNumber = useCallback((step: CheckoutStep): number => {
    let n = 0;
    if (isFastMode) { n++; if (step === 'email') return n; }
    n++; if (step === 'userData') return n;
    if (hasBillingStep) { n++; if (step === 'billing') return n; }
    n++; if (step === 'address') return n;
    n++; if (step === 'deliveryDate') return n;
    n++; return n; // payment
  }, [isFastMode, hasBillingStep]);

  // Step summaries
  const getStepSummary = (step: CheckoutStep): string => {
    switch (step) {
      case 'email': return user?.email || '';
      case 'userData': return user ? `${user.name} ${user.lastname}` : '';
      case 'billing': {
        if (!billingChosen) return '';
        if (billingCompanyId === null) return 'Personal';
        return organization?.companies.find(c => c.id === billingCompanyId)?.display_name || '';
      }
      case 'address': return selectedAddress?.address?.split(',')[0] || '';
      case 'deliveryDate': return deliveryDates.find(d => d.value === selectedDeliveryDate)?.label || '';
      case 'payment': return pMethods[selectedPaymentMethod || 0] || '';
      default: return '';
    }
  };

  // Email/OTP success
  const handleEmailSuccess = useCallback((u: CheckoutUser, addrs: CheckoutAddress[], pm: PaymentMethods, arol: boolean, po?: PendingOrder[]) => {
    setUser(u);
    setIsLoggedIn(true);
    if (addrs.length > 0) {
      setAddresses(addrs);
      setSelectedAddressId(addrs[0].id);
    }
    setPMethods(pm);
    setAccRecOverLimit(arol);
    if (po && po.length > 0) {
      setPendingOrders(po);
      setPendingOrdersResolved(false);
    }
    trackCheckoutStepCompleted('email', cart.total);
    if (!u.is_complete) {
      setCurrentStep('userData');
    } else {
      setCurrentStep(hasBillingStep ? 'billing' : 'address');
    }
  }, [cart.total, hasBillingStep]);

  // Pending orders gate resolved
  const handlePendingOrdersGateContinue = useCallback((remaining: PendingOrder[]) => {
    setPendingOrders(remaining);
    setPendingOrdersResolved(true);
  }, []);

  // API wrappers
  const sendOtp = useCallback(async (email: string) => {
    return apiCall(route('api.checkout.otp.send'), { method: 'POST', body: JSON.stringify({ email }) });
  }, [apiCall]);

  const verifyOtp = useCallback(async (email: string, otp: string) => {
    return apiCall(route('api.checkout.otp.verify'), { method: 'POST', body: JSON.stringify({ email, otp }) });
  }, [apiCall]);

  const updateUser = useCallback(async (data: Partial<CheckoutUser>) => {
    const result = await apiCall<{ user?: CheckoutUser }>(route('api.checkout.user.update'), {
      method: 'POST', body: JSON.stringify(data),
    });
    if (result.success && result.user) {
      setUser(result.user);
      if (result.user.is_complete) {
        setCurrentStep(hasBillingStep ? 'billing' : 'address');
      }
    }
    return result;
  }, [apiCall, addresses.length, hasBillingStep]);

  // Billing selection handler
  const handleBillingSelect = useCallback((companyId: number | null) => {
    setBillingCompanyId(companyId);
  }, []);

  const handleBillingContinue = useCallback(async () => {
    setBillingChosen(true);

    // Reset downstream selections
    setSelectedAddressId(null);
    setDeliveryDates([]);
    setSelectedDeliveryDate(null);
    setSelectedPaymentMethod(null);
    setAddressDiscountWarning(null);
    setSlotDiscountWarning(null);

    // Fetch payment methods for the selected company
    if (billingCompanyId !== null) {
      const result = await apiCall<{
        methods?: PaymentMethods;
        accRecOverLimit?: boolean;
        accRecName?: string | null;
        directPaymentDiscount?: { percent: number; savings: number } | null;
      }>(route('api.checkout.payment-methods-for-company'), {
        method: 'POST',
        body: JSON.stringify({ company_id: billingCompanyId }),
      });
      if (result.success) {
        if (result.methods) setPMethods(result.methods);
        if (result.accRecOverLimit !== undefined) setAccRecOverLimit(result.accRecOverLimit);
        setAccRecName(result.accRecName ?? null);
        setDirectPayDiscount(result.directPaymentDiscount ?? null);
      }
    } else {
      // Personal: restore initial payment methods
      setPMethods(initialPaymentMethods);
      setAccRecOverLimit(initialAccRecOverLimit);
      setAccRecName(initialAccRecName);
      setDirectPayDiscount(directPaymentDiscount ?? null);
    }

    trackCheckoutStepCompleted('billing', cart.total);
    setCurrentStep('address');
  }, [billingCompanyId, apiCall, cart.total, initialPaymentMethods, initialAccRecOverLimit, initialAccRecName, directPaymentDiscount]);

  // Address form success - refresh via API
  const handleAddressFormSuccess = useCallback(async (newAddressId?: number) => {
    const stateResult = await apiCall<{ user?: CheckoutUser; addresses?: CheckoutAddress[] }>(route('api.checkout.state'), { method: 'GET' });
    if (stateResult.success && stateResult.addresses) {
      setAddresses(stateResult.addresses);
      const addrId = newAddressId ?? stateResult.addresses[stateResult.addresses.length - 1]?.id;
      if (addrId) {
        setSelectedAddressId(addrId);
        setDeliveryDates([]);
        setSelectedDeliveryDate(null);
        setCurrentStep('deliveryDate');
      }
    }
  }, [apiCall]);

  // Address selection
  const handleAddressSelect = useCallback(async (addressId: number) => {
    setSelectedAddressId(addressId);
    setDeliveryDates([]);
    setSelectedDeliveryDate(null);

    // Check address compatibility and show/hide warning
    const address = addresses.find(a => a.id === addressId);
    if (address && !isAddressCompatibleWithDiscount(address)) {
      const displayName = cart.discountInfo?.display_name || cart.discountCode;
      if (discountLocationId) {
        const locationAddr = cart.discountInfo?.location_address || 'tu dirección registrada';
        setAddressDiscountWarning(`El descuento "${displayName}" solo aplica para entregas en ${locationAddr}. Si elegís esta dirección, el descuento no se aplicará.`);
      } else if (discountNeighborhood) {
        setAddressDiscountWarning(`El descuento "${displayName}" solo aplica para entregas en ${discountNeighborhood}. Si elegís esta dirección, el descuento no se aplicará.`);
      }
    } else {
      setAddressDiscountWarning(null);
    }

    // Re-fetch payment methods with address context (location may have its own AccRec)
    const body: Record<string, unknown> = { address_id: addressId };
    if (hasBillingStep && billingCompanyId !== null) {
      body.company_id = billingCompanyId;
    }
    const result = await apiCall<{
      methods?: PaymentMethods;
      accRecOverLimit?: boolean;
      accRecName?: string | null;
      directPaymentDiscount?: { percent: number; savings: number } | null;
    }>(route('api.checkout.payment-methods-for-company'), {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (result.success) {
      if (result.methods) setPMethods(result.methods);
      if (result.accRecOverLimit !== undefined) setAccRecOverLimit(result.accRecOverLimit);
      setAccRecName(result.accRecName ?? null);
      setDirectPayDiscount(result.directPaymentDiscount ?? null);
    }
  }, [addresses, discountNeighborhood, discountLocationId, cart.discountCode, cart.discountInfo, isAddressCompatibleWithDiscount, hasBillingStep, billingCompanyId, apiCall]);

  // Load delivery dates
  const handleLoadDates = useCallback(async (addressId: number) => {
    const result = await apiCall<{ dates?: DeliveryDate[]; isDeliverable?: boolean; preferredDeliveryDate?: string | null }>(
      `${route('api.checkout.address.dates', { id: addressId })}`,
      { method: 'GET' },
    );
    if (result.success && result.dates) {
      setDeliveryDates(result.dates);
      if (result.dates.length > 0) {
        // For nearby offers, force-select the discount's delivery slot
        const slotMatch = discountDeliverySlot ? result.dates.find(d => d.value === discountDeliverySlot) : null;
        const preferred = result.preferredDeliveryDate;
        const prefMatch = preferred ? result.dates.find(d => d.value === preferred) : null;
        setSelectedDeliveryDate(slotMatch?.value ?? prefMatch?.value ?? result.dates[0].value);
      }
    }
    return result;
  }, [apiCall, discountDeliverySlot]);

  // Payment method selection with GA tracking
  const handleSelectPaymentMethod = useCallback((method: number) => {
    setSelectedPaymentMethod(method);
    try {
      if (typeof dataLayer !== 'undefined') {
        const paymentMethodName = pMethods[method] || 'Unknown';
        dataLayer.push({ ecommerce: null });
        dataLayer.push({
          event: "add_payment_info",
          ecommerce: { ...ga, payment_type: paymentMethodName },
        });
      }
    } catch (e) {
      console.error('Error tracking add_payment_info:', e);
    }
  }, [pMethods, ga]);

  // Order submission
  const handleSubmitOrder = useCallback(async () => {
    if (!selectedAddressId || !selectedDeliveryDate || selectedPaymentMethod === null) return;
    if (hasUnfulfilledMinimum) return;

    setIsSubmitting(true);
    setSubmitError(null);

    trackCheckoutStepCompleted('payment', cart.total);

    // Fire begin_checkout event
    try {
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({ ecommerce: null });
        dataLayer.push({ event: "begin_checkout", ecommerce: ga });
      }
    } catch (e) {
      console.error('Error tracking begin_checkout:', e);
    }

    // Pack IDs from localStorage
    let packIds: number[] = [];
    try {
      const stored = localStorage.getItem('cart_packs');
      if (stored) packIds = Object.keys(JSON.parse(stored)).map(Number);
    } catch (_) {}

    try {
      const result = await apiCall<{ redirect?: string }>(route('api.checkout.order.submit'), {
        method: 'POST',
        body: JSON.stringify({
          address_id: selectedAddressId,
          delivery_date: selectedDeliveryDate,
          payment_method: selectedPaymentMethod,
          cellphone: user?.cellphone || '',
          comment,
          pack_ids: packIds,
          checkout_type: isFastMode ? 'fast' : 'normal',
          // QR shares the Mercado Pago method id, so the backend cannot tell
          // them apart on its own — and QR carries no wholesale surcharge.
          pay_with_qr: useQr,
          ...(hasBillingStep && billingChosen ? { billing_company_id: billingCompanyId } : {}),
        }),
      });

      if (result.success && result.redirect) {
        // If QR was selected, rewrite the MP payment redirect to the QR page
        if (useQr) {
          const match = result.redirect.match(/\/compras\/(\d+)\/pagar/);
          if (match) {
            window.location.href = `/compras/${match[1]}/pagar-qr`;
            return;
          }
        }
        window.location.href = result.redirect;
      } else {
        setSubmitError(result.message);
        setIsSubmitting(false);
      }
    } catch (err) {
      setSubmitError('Error de conexión. Intentá de nuevo.');
      setIsSubmitting(false);
    }
  }, [selectedAddressId, selectedDeliveryDate, selectedPaymentMethod, user?.cellphone, comment, apiCall, ga, hasUnfulfilledMinimum, hasBillingStep, billingChosen, billingCompanyId, useQr]);

  // Pending orders gate — shown before checkout when user has recent pending orders
  if (isLoggedIn && pendingOrders.length > 0 && !pendingOrdersResolved) {
    return (
      <MainLayout>
        <Head title="Pedidos pendientes">
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <PendingOrdersGate
          pendingOrders={pendingOrders}
          onContinue={handlePendingOrdersGateContinue}
        />
      </MainLayout>
    );
  }

  // Quick checkout view for eligible users
  if (showQuickCheckout && quickCheckout && !cart.isEmpty) {
    return (
      <QuickCheckoutView
        quickCheckout={quickCheckout}
        cart={cart}
        cellphone={initialUser?.cellphone || ''}
        onModify={async (step?: CheckoutStep) => {
          setSelectedAddressId(quickCheckout.addressId);
          setSelectedPaymentMethod(quickCheckout.paymentMethod);

          if (step === 'payment') {
            setSelectedDeliveryDate(quickCheckout.deliveryDate);
          }

          // Pre-set billing from quick checkout defaults
          if (hasBillingStep) {
            if (quickCheckout.billingCompanyId) {
              setBillingCompanyId(quickCheckout.billingCompanyId);
            }
            // Mark billing as chosen (personal or company) unless
            // the user explicitly wants to modify billing
            if (step !== 'billing') {
              setBillingChosen(true);
            }
          }

          // Fetch payment methods with the quick checkout's company and address
          const body: Record<string, unknown> = { address_id: quickCheckout.addressId };
          if (quickCheckout.billingCompanyId) {
            body.company_id = quickCheckout.billingCompanyId;
          }
          const result = await apiCall<{
            methods?: PaymentMethods;
            accRecOverLimit?: boolean;
            accRecName?: string | null;
            directPaymentDiscount?: { percent: number; savings: number } | null;
          }>(route('api.checkout.payment-methods-for-company'), {
            method: 'POST',
            body: JSON.stringify(body),
          });
          if (result.success) {
            if (result.methods) setPMethods(result.methods);
            if (result.accRecOverLimit !== undefined) setAccRecOverLimit(result.accRecOverLimit);
            setAccRecName(result.accRecName ?? null);
            setDirectPayDiscount(result.directPaymentDiscount ?? null);
          }

          // Push a history entry that includes Inertia's page data so
          // Inertia doesn't get confused by it. The _checkoutModify flag
          // lets the popstate listener know this is the "full form" entry.
          const inertiaState = window.history.state ?? {};
          window.history.pushState(
            { ...inertiaState, _checkoutModify: true },
            '',
            window.location.href,
          );
          setShowQuickCheckout(false);
          setCurrentStep(step || (hasBillingStep ? 'billing' : 'address'));
        }}
        ga={ga}
        productsPageUrl={route('products.index')}
      />
    );
  }

  if (cart.isEmpty) {
    return (
      <MainLayout>
        <Head title="Checkout">
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <div className="mx-auto max-w-[600px] px-4 pb-10 pt-6">
          <CartSummary cart={cart} productsPageUrl={route('products.index')} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Head title="Checkout">
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className="mx-auto max-w-[600px] px-4 pb-10 pt-6">
        <div className="py-4 text-center">
          <h1 className="m-0 text-2xl font-bold text-gray-800">Completá tu pedido</h1>
          <p className="mt-2 text-sm text-gray-500">Seguí los pasos para confirmar tu compra</p>
        </div>

        <CartSummary
          cart={adjustedCart}
          wholesaleSurcharge={wholesaleSurcharge}
          productsPageUrl={route('products.index')}
          allowsDiscountCode={allowsDiscountCode}
          discountError={discountError}
          isLoadingDiscount={isLoadingDiscount}
          onPreviewDiscount={handlePreviewDiscount}
          onConfirmDiscount={handleConfirmDiscount}
          onCancelPreview={handleCancelPreview}
          onApplyDiscount={handleApplyDiscount}
          onClearDiscount={handleClearDiscount}
          previewData={previewData}
        />

        {isGiftMode && giftData && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🎁</span>
              <span className="font-semibold text-green-800">Este pedido es un regalo</span>
            </div>
            <div className="text-sm text-green-700 space-y-1">
              <p>Para: <strong>{giftData.recipient_name}</strong></p>
              <p>De parte de: <strong>{giftData.from}</strong></p>
              {giftData.message && <p>Mensaje: <em>"{giftData.message}"</em></p>}
            </div>
          </div>
        )}

        {hasUnfulfilledMinimum && (
          <MinOrderAlert minOrder={minOrder} currentTotal={cartSubtotal} />
        )}

        {/* Step 1: Email/OTP (fast mode only) */}
        {isFastMode && (
          <AccordionSection
            title="Email"
            stepNumber={stepNumber('email')}
            isActive={currentStep === 'email'}
            isComplete={isStepComplete('email')}
            canActivate={canProceedToStep('email')}
            summary={getStepSummary('email')}
            onActivate={() => handleStepClick('email')}
          >
            <EmailOtpSection
              onSuccess={handleEmailSuccess}
              sendOtp={sendOtp}
              verifyOtp={verifyOtp}
              isLoading={apiLoading}
              initialEmail={user?.email || ''}
              isLoggedIn={isLoggedIn}
            />
          </AccordionSection>
        )}

        {/* User Data */}
        <AccordionSection
          title="Tus datos"
          stepNumber={stepNumber('userData')}
          isActive={currentStep === 'userData'}
          isComplete={isStepComplete('userData')}
          canActivate={canProceedToStep('userData')}
          summary={getStepSummary('userData')}
          onActivate={() => handleStepClick('userData')}
        >
          {user && (
            <UserDataSection
              user={user}
              onSuccess={(u) => {
                setUser(u);
                if (u.is_complete) {
                  trackCheckoutStepCompleted('userData', cart.total);
                  setCurrentStep(hasBillingStep ? 'billing' : 'address');
                }
              }}
              updateUser={updateUser}
              isLoading={apiLoading}
            />
          )}
        </AccordionSection>

        {/* Billing (org users only) */}
        {hasBillingStep && (
          <AccordionSection
            title="Facturación"
            stepNumber={stepNumber('billing')}
            isActive={currentStep === 'billing'}
            isComplete={isStepComplete('billing')}
            canActivate={canProceedToStep('billing')}
            summary={getStepSummary('billing')}
            onActivate={() => handleStepClick('billing')}
          >
            <BillingSection
              companies={organization!.companies}
              selectedCompanyId={billingCompanyId}
              personalDisabled={personalDisabled}
              billingChosen={billingChosen}
              discountWarning={
                cart.discountInfo?.companies_only && billingCompanyId === null
                  ? `El descuento "${cart.discountInfo?.display_name || cart.discountCode}" solo aplica a compras a nombre de una empresa. Si elegís Personal, el descuento no se aplicará.`
                  : null
              }
              onSelect={handleBillingSelect}
              onContinue={handleBillingContinue}
              isLoading={apiLoading}
            />
          </AccordionSection>
        )}

        {/* Address */}
        <AccordionSection
          title="Dirección de entrega"
          stepNumber={stepNumber('address')}
          isActive={currentStep === 'address'}
          isComplete={isStepComplete('address')}
          canActivate={canProceedToStep('address')}
          summary={getStepSummary('address')}
          onActivate={() => handleStepClick('address')}
        >
          <AddressSection
            addresses={filteredAddresses}
            selectedAddressId={selectedAddressId}
            onSelectAddress={handleAddressSelect}
            onFormSuccess={handleAddressFormSuccess}
            onContinue={() => {
              trackCheckoutStepCompleted('address', cart.total);
              setCurrentStep('deliveryDate');
            }}
            isLoading={apiLoading}
            routes={{ resolve: route('api.location.resolve'), create: route('api.location.store') }}
            discountNeighborhood={discountNeighborhood}
            isAddressCompatible={isAddressCompatibleWithDiscount}
            discountWarning={addressDiscountWarning}
            hideAddButton={personalDisabled || (hasBillingStep && billingChosen && billingCompanyId !== null)}
            orgManageUrl={hasBillingStep ? route('organization.index') : undefined}
          />
        </AccordionSection>

        {/* Delivery Date */}
        <AccordionSection
          title="Fecha de entrega"
          stepNumber={stepNumber('deliveryDate')}
          isActive={currentStep === 'deliveryDate'}
          isComplete={isStepComplete('deliveryDate')}
          canActivate={canProceedToStep('deliveryDate')}
          summary={getStepSummary('deliveryDate')}
          onActivate={() => handleStepClick('deliveryDate')}
        >
          <DeliveryDateSection
            selectedAddress={selectedAddress}
            deliveryDates={deliveryDates}
            selectedDate={selectedDeliveryDate}
            onSelectDate={setSelectedDeliveryDate}
            onLoadDates={handleLoadDates}
            onContinue={() => {
              trackCheckoutStepCompleted('deliveryDate', cart.total);
              setCurrentStep('payment');
            }}
            isLoading={apiLoading}
            isDateCompatible={discountDeliverySlot ? isDeliveryDateCompatibleWithDiscount : undefined}
            discountWarning={slotDiscountWarning}
          />
        </AccordionSection>

        {/* Payment */}
        <AccordionSection
          title="Pago"
          stepNumber={stepNumber('payment')}
          isActive={currentStep === 'payment'}
          isComplete={isStepComplete('payment')}
          canActivate={canProceedToStep('payment')}
          summary={getStepSummary('payment')}
          onActivate={() => handleStepClick('payment')}
        >
          <PaymentSection
            cart={adjustedCart}
            wholesaleSurchargePercent={hasWholesaleItems ? cart.wholesaleSurchargePercent! : 0}
            wholesaleSurcharge={wholesaleSurcharge}
            paymentMethods={pMethods}
            accRecOverLimit={accRecOverLimit}
            accRecName={accRecName}
            mercadoPagoEnabled={mercadoPagoEnabled}
            selectedMethod={selectedPaymentMethod}
            comment={comment}
            onSelectMethod={handleSelectPaymentMethod}
            onQrSelected={setUseQr}
            onCommentChange={setComment}
            onSubmit={handleSubmitOrder}
            isSubmitting={isSubmitting}
            error={submitError}
            directPaymentDiscount={directPayDiscount}
            showQrOption={serverShowQr && typeof navigator !== 'undefined' && !/Mobile|Android|iPhone|iPad/i.test(navigator.userAgent)}
          />
        </AccordionSection>
      </div>
    </MainLayout>
  );
}
